/**
 * AI 智能分类 · 副作用 middleware
 *
 * v3 阶段曾计划用 Redux middleware 监听 startSession.fulfilled 建立 EventSource。
 * v4 SSE 改为组件层直接持有客户端（services/aiClassifySse.ts），原 SSE 逻辑下线。
 *
 * v4.5 起本文件接管「task slice 持久化」副作用：
 *   · 任意 aiClassify/task/* action 之后 throttle 写一次 localStorage
 *   · 启动时一次性 hydrate（在 AIChatDrawer 挂载阶段做）
 */

import type { Middleware } from 'redux'
import { saveTasksToStorage } from './taskPersistence'

const TASK_ACTION_PREFIX = 'aiClassify/task/'
const SAVE_THROTTLE_MS = 800

let lastSaveAt = 0
let pendingTimer: number | null = null

export const streamMiddleware: Middleware =
  (storeApi) => (next) => (action) => {
    const result = next(action)

    // 仅监听 task slice action，避免无关 dispatch 触发 localStorage 写入
    if (
      typeof action === 'object' &&
      action !== null &&
      typeof (action as { type?: unknown }).type === 'string' &&
      ((action as { type: string }).type as string).startsWith(
        TASK_ACTION_PREFIX
      )
    ) {
      scheduleSave(storeApi)
    }
    return result
  }

function scheduleSave(storeApi: { getState: () => unknown }): void {
  const now = Date.now()
  const elapsed = now - lastSaveAt
  if (elapsed >= SAVE_THROTTLE_MS) {
    persist(storeApi)
    return
  }
  if (pendingTimer !== null) return
  pendingTimer = window.setTimeout(() => {
    pendingTimer = null
    persist(storeApi)
  }, SAVE_THROTTLE_MS - elapsed) as unknown as number
}

function persist(storeApi: { getState: () => unknown }): void {
  lastSaveAt = Date.now()
  try {
    const state = storeApi.getState() as {
      aiClassify?: {
        task?: {
          tasks?: unknown[]
          hasUnreadCompletion?: boolean
          pendingNotifications?: string[]
        }
      }
    }
    const t = state.aiClassify?.task
    if (!t) return
    saveTasksToStorage({
      tasks: (t.tasks as never) || [],
      hasUnreadCompletion: !!t.hasUnreadCompletion,
      pendingNotifications: Array.isArray(t.pendingNotifications)
        ? t.pendingNotifications
        : []
    })
  } catch {
    /* silent */
  }
}
